import { useState } from 'react';
import { CheckCircle2, XCircle, ArrowRight, RotateCcw, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const QuizEngine = ({ 
  questions, 
  title,
  introText,
  resultMessages,
  relatedLinks = []
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);

  const handleStart = () => {
    setGameStarted(true);
  };

  const handleAnswer = (answerIndex) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(answerIndex);
    const isCorrect = answerIndex === questions[currentQuestion].correctAnswer;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    setAnswers(prev => [...prev, { 
      questionIndex: currentQuestion, 
      answerIndex, 
      isCorrect 
    }]);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      setShowResult(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setGameStarted(false);
  };

  const getResultMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80) return resultMessages?.excellent || 'Ausgezeichnet! Du kennst dich bestens aus.';
    if (percentage >= 60) return resultMessages?.good || 'Gut gemacht! Du hast ein solides Grundwissen.';
    if (percentage >= 40) return resultMessages?.average || 'Nicht schlecht, aber da geht noch mehr!';
    return resultMessages?.needsWork || 'Hier gibt es noch Lernpotenzial. Schau dir unsere Ratgeber an!';
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Intro screen
  if (!gameStarted) {
    return (
      <div className="quiz-container" data-testid="quiz-intro">
        <div className="text-center py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
            {title}
          </h1>
          {introText && (
            <p className="text-lg text-[#475569] mb-8 max-w-xl mx-auto">
              {introText}
            </p>
          )}
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-8 max-w-md mx-auto">
            <div className="text-5xl font-bold text-[#0284C7] mb-2 font-[var(--font-heading)]">
              {questions.length}
            </div>
            <div className="text-[#475569]">Fragen warten auf dich</div>
          </div>
          <button
            onClick={handleStart}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
            data-testid="quiz-start-button"
          >
            Quiz starten
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Result screen
  if (showResult) {
    return (
      <div className="quiz-container" data-testid="quiz-result">
        <div className="result-container animate-scaleIn">
          <div className="score-reveal">
            <div className="result-score">{score}/{questions.length}</div>
            <div className="result-label">Punkte erreicht</div>
          </div>
          <p className="result-message">{getResultMessage()}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRestart}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0284C7] text-[#0284C7] font-semibold rounded-lg hover:bg-[#0284C7]/5 transition-colors duration-200"
              data-testid="quiz-restart-button"
            >
              <RotateCcw size={18} />
              Nochmal spielen
            </button>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: title,
                    text: `Ich habe ${score} von ${questions.length} Punkten beim "${title}" Quiz auf tippspiel.it erreicht!`,
                    url: window.location.href,
                  });
                }
              }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
              data-testid="quiz-share-button"
            >
              <Share2 size={18} />
              Ergebnis teilen
            </button>
          </div>

          {relatedLinks.length > 0 && (
            <div className="internal-links mt-8">
              <div className="internal-links-title">Das könnte dich auch interessieren:</div>
              <div className="internal-links-list">
                {relatedLinks.map((link, index) => (
                  <Link 
                    key={index} 
                    to={link.path} 
                    className="internal-link"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Question screen
  const question = questions[currentQuestion];

  return (
    <div className="quiz-container" data-testid="quiz-question">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-[#475569] mb-2">
          <span>Frage {currentQuestion + 1} von {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="quiz-progress">
          <div 
            className="quiz-progress-bar progress-bar" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 md:p-8">
        <h2 className="quiz-question" data-testid="quiz-question-text">
          {question.question}
        </h2>

        {question.image && (
          <div className="mb-6 rounded-lg overflow-hidden">
            <img 
              src={question.image} 
              alt="Frage Bild" 
              className="w-full h-48 md:h-64 object-cover"
            />
          </div>
        )}

        <div className="quiz-options">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === question.correctAnswer;
            const showFeedback = selectedAnswer !== null;
            
            let optionClass = 'quiz-option quiz-option-btn';
            if (showFeedback) {
              if (isCorrect) optionClass += ' correct';
              else if (isSelected && !isCorrect) optionClass += ' incorrect';
            } else if (isSelected) {
              optionClass += ' selected';
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                className={optionClass}
                disabled={selectedAnswer !== null}
                data-testid={`quiz-option-${index}`}
              >
                <span className="quiz-option-letter">
                  {String.fromCharCode(65 + index)}
                </span>
                <span className="flex-1">{option}</span>
                {showFeedback && isCorrect && (
                  <CheckCircle2 className="text-green-500 flex-shrink-0" size={20} />
                )}
                {showFeedback && isSelected && !isCorrect && (
                  <XCircle className="text-red-500 flex-shrink-0" size={20} />
                )}
              </button>
            );
          })}
        </div>

        {selectedAnswer !== null && (
          <div className="mt-6 animate-fadeInUp">
            {question.explanation && (
              <div className="bg-[#F1F5F9] rounded-lg p-4 mb-4">
                <p className="text-sm text-[#475569]">
                  <strong>Erklärung:</strong> {question.explanation}
                </p>
              </div>
            )}
            <button
              onClick={handleNext}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
              data-testid="quiz-next-button"
            >
              {currentQuestion < questions.length - 1 ? 'Nächste Frage' : 'Ergebnis anzeigen'}
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizEngine;
